
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DeviceOverview } from "./tabs/DeviceOverview";
import { DeviceConfig } from "./tabs/DeviceConfig";
import { DeviceNotifications } from "./tabs/DeviceNotifications";
import { DeviceLocation } from "./tabs/DeviceLocation";
import { DevicePastImages } from "./tabs/DevicePastImages";
import { DeviceLocationManager } from "./tabs/DeviceLocationManager";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useRealtimeDeviceDetails } from "@/hooks/use-realtime-device-details";

interface DeviceDetailsProps {
  deviceId: string;
}

export const DeviceDetails = ({ deviceId }: DeviceDetailsProps) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("overview");
  const { device, isLoading, refreshDevice } = useRealtimeDeviceDetails(deviceId);

  if (isLoading || !device) {
    return <div>Loading device details...</div>;
  }

  console.log("Device data in DeviceDetails:", device); // Debug log to verify data structure

  // Check if public view is enabled to conditionally show Location Manager tab
  const isPublicEnabled = device.enablePublic === 1 || device.enablePublic === "1";

  const baseTabs = [
    { value: "overview", label: "Overview" },
    { value: "past-images", label: "Past Images" },
    { value: "config", label: "Configuration" },
    { value: "notifications", label: "AI Setup" },
    { value: "location", label: "Location" },
  ];

  // Add Location Manager tab conditionally when public view is enabled
  const tabs = isPublicEnabled 
    ? [...baseTabs, { value: "location-manager", label: "Location Manager" }]
    : baseTabs;

  if (isMobile) {
    return (
      <div className="w-full space-y-4 flex flex-col items-center max-w-full pb-6">
        <div className="flex flex-wrap justify-center w-full px-3 sm:px-4">
          <div className="grid grid-cols-2 gap-1 mb-1 w-full">
            {tabs.slice(0, 2).map((tab) => (
              <Button
                key={tab.value}
                variant={activeTab === tab.value ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTab(tab.value)}
                className={`text-xs whitespace-nowrap ${
                  activeTab === tab.value 
                    ? "text-black border-[#DFEF97]" 
                    : ""
                }`}
                style={activeTab === tab.value ? { backgroundColor: '#DFEF97' } : {}}
              >
                {tab.label}
              </Button>
            ))}
          </div>
          <div className={`grid gap-1 w-full ${tabs.length > 5 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {tabs.slice(2).map((tab) => (
              <Button
                key={tab.value}
                variant={activeTab === tab.value ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTab(tab.value)}
                className={`text-xs whitespace-nowrap ${
                  activeTab === tab.value 
                    ? "text-black border-[#DFEF97]" 
                    : ""
                }`}
                style={activeTab === tab.value ? { backgroundColor: '#DFEF97' } : {}}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="w-full max-w-full px-3 sm:px-4">
          {activeTab === "overview" && <DeviceOverview device={device} />}
          {activeTab === "config" && (
            <DeviceConfig 
              device={device} 
              deviceId={deviceId} 
              onUpdate={refreshDevice}
            />
          )}
          {activeTab === "notifications" && (
            <DeviceNotifications 
              device={device} 
              deviceId={deviceId} 
              onUpdate={refreshDevice}
            />
          )}
          {activeTab === "location" && <DeviceLocation device={device} />}
          {activeTab === "past-images" && <DevicePastImages device={{ device_id: deviceId }} />}
          {activeTab === "location-manager" && isPublicEnabled && (
            <DeviceLocationManager 
              device={device} 
              deviceId={deviceId} 
              onUpdate={refreshDevice}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <Tabs defaultValue="overview" className="w-full max-w-full">
      <TabsList className={`grid w-full ${tabs.length > 5 ? 'grid-cols-6' : 'grid-cols-5'}`}>
        {tabs.map((tab) => (
          <TabsTrigger 
            key={tab.value} 
            value={tab.value}
            className="data-[state=active]:text-black data-[state=active]:bg-[#DFEF97] data-[state=active]:border-[#DFEF97]"
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="overview" className="overflow-x-hidden">
        <DeviceOverview device={device} />
      </TabsContent>
      <TabsContent value="past-images">
        <DevicePastImages device={{ device_id: deviceId }} />
      </TabsContent>
      <TabsContent value="config" className="overflow-x-hidden">
        <DeviceConfig 
          device={device} 
          deviceId={deviceId} 
          onUpdate={refreshDevice}
        />
      </TabsContent>
      <TabsContent value="notifications" className="overflow-x-hidden">
        <DeviceNotifications 
          device={device} 
          deviceId={deviceId} 
          onUpdate={refreshDevice}
        />
      </TabsContent>
      <TabsContent value="location" className="overflow-x-hidden">
        <DeviceLocation device={device} />
      </TabsContent>
      {isPublicEnabled && (
        <TabsContent value="location-manager" className="overflow-x-hidden">
          <DeviceLocationManager 
            device={device} 
            deviceId={deviceId} 
            onUpdate={refreshDevice}
          />
        </TabsContent>
      )}
    </Tabs>
  );
}
